// src/components/charts/ProgressCharts.jsx
import React from "react";
import { UsersGroupIcon } from "../../assets/svgs/icons";
import { getDashboardStatsView } from "../../utils/dashboardStats";
import { Spin } from "antd";

export const SemiCircleProgress = ({ statsData }) => {
  const stats = getDashboardStatsView(statsData);
  const totalForms = stats.totalForms;
  const completedForms = stats.completeForms;

  // Calculate percentage for the progress arc
  const progress = totalForms > 0 ? (completedForms / totalForms) * 100 : 0;

  // Calculate strokeDashoffset (408 is total path length)
  const strokeDashoffset = 408 - (progress / 100) * 408;

  return (
    <div className="relative flex justify-center items-center h-46 bg-white rounded-2xl">
      <svg width="300" height="120" viewBox="0 0 300 150">
        {/* Background Arc */}
        <path
          d="M20 150 A130 130 0 0 1 280 150"
          fill="none"
          stroke="#F3F4F6"
          strokeWidth="20"
          strokeLinecap="round"
        />
        {/* Progress Arc */}
        <path
          d="M20 150 A130 130 0 0 1 280 150"
          fill="none"
          stroke="#10B981"
          strokeWidth="20"
          strokeLinecap="round"
          strokeDasharray="408"
          strokeDashoffset={strokeDashoffset}
        />
        {/* Center Text */}
        <text
          x="150"
          y="120"
          textAnchor="middle"
          fill="#10B981"
          className="text-2xl font-medium"
        >
          {completedForms}
        </text>
        <text
          x="150"
          y="140"
          textAnchor="middle"
          fill="#6B7280"
          className="text-sm"
        >
          Total Completed Forms
        </text>
      </svg>
    </div>
  );
};

export default SemiCircleProgress;

export const CircularProgress = ({ data, isLoading }) => {
  const stats = getDashboardStatsView(data);
  const completedAssignments = stats.completedAssignments;
  const totalAssignments = stats.totalAssignments;
  const pendingAssignments =
    stats.assignedAssignments ||
    (totalAssignments > completedAssignments
      ? totalAssignments - completedAssignments
      : 0);

  // Show loading state
  if (isLoading) {
    return (
      <div className="relative flex justify-center items-center rounded-xl bg-white h-[300px]">
        <Spin size="large" />
      </div>
    );
  }

  // Calculate percentages
  const totalCircumference = 628; // 2 * π * r (where r = 100)
  const completedPercentage = totalAssignments
    ? (completedAssignments / totalAssignments) * 100
    : 0;
  const pendingPercentage = totalAssignments
    ? (pendingAssignments / totalAssignments) * 100
    : 0;

  // Calculate stroke dash offsets
  const completedOffset =
    totalCircumference - (completedPercentage / 100) * totalCircumference;
  const pendingOffset =
    totalCircumference - (pendingPercentage / 100) * totalCircumference;

  // Format number for display (e.g., 1200 -> 1.2k)
  const formatNumber = (num) => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}k`;
    }
    return num.toString();
  };

  return (
    <div className="relative flex justify-center items-center rounded-xl bg-white">
      <div className="div">
        <svg width="240" height="190" viewBox="0 0 240 240">
          {/* Main circle */}
          <circle
            cx="120"
            cy="120"
            r="100"
            fill="none"
            stroke="#CDFDE9"
            strokeWidth="20"
            strokeLinecap="round"
          />
          {/* Completed assignments circle */}
          <circle
            cx="120"
            cy="120"
            r="100"
            fill="none"
            stroke="#10B981"
            strokeWidth="20"
            strokeDasharray={totalCircumference}
            strokeDashoffset={completedOffset}
            transform="rotate(-90 120 120)"
            strokeLinecap="round"
          />
          {/* Pending assignments circle */}
          <circle
            cx="120"
            cy="120"
            r="100"
            fill="none"
            stroke="#3B82F6"
            strokeWidth="20"
            strokeDasharray={totalCircumference}
            strokeDashoffset={pendingOffset}
            transform="rotate(-90 120 120)"
            strokeLinecap="round"
          />
          {/* Center text */}
          <text
            x="120"
            y="130"
            textAnchor="middle"
            className="text-3xl font-semibold"
            fill="#10B981"
          >
            {formatNumber(completedAssignments)}
          </text>
        </svg>
        <div className="text-center">
          <p className="text-sm text-gray-500 mb-2">Completed Assignments</p>
          {/* Optional: Add legend */}
          <div className="flex justify-center gap-4 text-xs">
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-[#10B981]"></span>
              <span>
                Completed ({Math.round(completedPercentage)}
                %)
              </span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-[#3B82F6]"></span>
              <span>Pending ({Math.round(pendingPercentage)}%)</span>
            </div>
          </div>
          <br />
        </div>
      </div>
    </div>
  );
};

export const CampaignProgress = ({ data, isLoading }) => {
  // SVG parameters
  const size = 145;
  const strokeWidth = 10;
  const center = size / 2;

  // Define two different radii for outer and inner circles
  const outerRadius = center - strokeWidth / 2;
  const innerRadius = center - strokeWidth * 2;

  // Calculate circumferences
  const outerCircumference = 2 * Math.PI * outerRadius;
  const innerCircumference = 2 * Math.PI * innerRadius;

  // Get actual values from data or use fallbacks
  const stats = getDashboardStatsView(data);
  const activeCampaigns = stats.activeCampaigns;
  const totalCampaigns = stats.totalCampaigns || 1;
  const totalParticipants = stats.totalParticipants;

  // Calculate percentages for the circle progress
  const campaignActivePercent =
    Math.min(100, Math.round((activeCampaigns / totalCampaigns) * 100)) || 0;

  // For participants, use a more reasonable scale - assume a max of 1000 participants for 100%
  // This prevents the circle from being completely filled with just a few participants
  const maxExpectedParticipants = 1000;
  const participationPercent =
    Math.min(
      100,
      Math.round((totalParticipants / maxExpectedParticipants) * 100)
    ) || 0;

  // Calculate stroke-dasharray and stroke-dashoffset for both circles
  const blueOffset =
    outerCircumference - (campaignActivePercent / 100) * outerCircumference;
  const orangeOffset =
    innerCircumference - (participationPercent / 100) * innerCircumference;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center w-full h-full bg-white rounded-lg">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center w-full h-full bg-white rounded-lg">
      <div className="relative">
        {/* {totalCampaigns} */}
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {/* Outer base circle */}
          <circle
            cx={center}
            cy={center}
            r={outerRadius}
            fill="none"
            stroke="#f0f0f0"
            strokeWidth={strokeWidth}
          />

          {/* Inner base circle */}
          <circle
            cx={center}
            cy={center}
            r={innerRadius}
            fill="none"
            stroke="#f0f0f0"
            strokeWidth={strokeWidth}
          />

          {/* Blue progress (outer circle) - Campaign active rate */}
          <circle
            cx={center}
            cy={center}
            r={outerRadius}
            fill="none"
            stroke="#F97316"
            strokeWidth={strokeWidth}
            strokeDasharray={outerCircumference}
            strokeDashoffset={blueOffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
            style={{
              transition: "stroke-dashoffset 0.5s ease-in-out",
            }}
          />

          {/* Orange progress (inner circle) - Participation count */}
          <circle
            cx={center}
            cy={center}
            r={innerRadius}
            fill="none"
            stroke="#3B82F6"
            strokeWidth={strokeWidth}
            strokeDasharray={innerCircumference}
            strokeDashoffset={orangeOffset}
            strokeLinecap="round"
            transform={`rotate(-90 ${center} ${center})`}
            style={{
              transition: "stroke-dashoffset 0.5s ease-in-out",
            }}
          />
        </svg>

        {/* Center icon and counts */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
          <UsersGroupIcon className="w-6 h-6 text-gray-600 mb-1" />
        </div>
      </div>
    </div>
  );
};
